import React from 'react';

import { useSelector } from 'react-redux';

import { Body } from '@iris/react-components/Body';
import { Card } from '@iris/react-components/Card';
import { Image } from '@iris/react-components/Image';
import { Link } from '@iris/react-components/Link';
import { ArrowTopRightOnSquareIcon } from '@iris/react-icons/ArrowTopRightOnSquareIcon';

import {
    Button,
    Glyphicon,
    SubHeading,
} from 'reaxl';
import { DelayedImpressionObserver, sendClick, sendImpressions } from 'reaxl-analytics';
import { ndsInsuranceSponsorCtaClick } from 'reaxl-analytics-handlers';

import inventoryDuck from '@/ducks/inventoryDuck.js';

const PriceSectionInsurance = React.memo(({
    vin,
    logoMap,
    sponsorName,
    ctaHref,
    handleImpression,
    handleCtaClick,
}) => (
    <Card
        variant="subtle"
        data-cmp="sponsored-insurance-container"
    >
        <Card.Content>
            <DelayedImpressionObserver
                key={`sponsored-insurance-container-${vin}`}
                onChange={handleImpression}
                delay={0}
                id="sponsored-insurance-container"
            >
                <div className="iris:flex iris:gap-4 iris:items-start">
                    <div className="iris:flex iris:flex-shrink-0 iris:pt-1">
                        <Image
                            alt={`${sponsorName} Logo`}
                            src={`https://www.autotrader.com/cm-api/content/static/img/icon/logos/${logoMap[sponsorName?.toLowerCase()]}`}
                            height={32}
                            width={32}
                        />
                    </div>
                    <Body
                        className="iris:flex-1"
                        size="sm"
                        as="div"
                    >
                        <Body
                            className="iris:mb-1 iris:font-bold!"
                            size="sm"
                            as="p"
                        >
                            Car Insurance Made Simple
                        </Body>
                        <Link
                            href={ctaHref}
                            onClick={handleCtaClick}
                            target="_blank"
                            variant="inline"
                            className="iris:inline-flex!" // needs to be removed if variant works correctly again
                            size="sm"
                            icon={<ArrowTopRightOnSquareIcon />}
                            iconPosition="right"
                        >
                            Get a quote
                        </Link>
                        {' '}
                        for this vehicle in under 5 minutes.
                    </Body>
                </div>
            </DelayedImpressionObserver>
        </Card.Content>
    </Card>
));

const SponsoredInsuranceContainer = React.memo(({
    externalHost,
    id: listingId,
    logoMap,
    sponsorName,
    vin,
    vinParam,
    zip,
    zipParam,
    uiContext,
}) => {

    const inventory = useSelector(inventoryDuck.selectors.selectHydratedInventory);
    const owner = inventory[listingId]?.owner;
    const ownerId = owner?.id;
    const isPriceSection = uiContext === 'price-section';

    const eventData = {
        listingId,
        sponsor: sponsorName,
        ownerId,
        zip,
    };

    const handleImpression = () => {
        sendImpressions({ name: 'ndsInsuranceSponsorInviewImpression', data: { ...eventData } });
    };

    const buildHref = () => {
        let host = `${externalHost}&${vinParam}=${vin}`;

        if (zipParam && zip) {
            host += `&${zipParam}=${zip}`;
        }

        return host;
    };

    const ctaHref = buildHref();

    const handleCtaClick = (event) => {
        sendClick(ndsInsuranceSponsorCtaClick, event, { ...eventData });
    };

    // Render differently if FF vdp_new_price_section is enabled
    if (isPriceSection) {
        return (
            <PriceSectionInsurance
                vin={vin}
                logoMap={logoMap}
                sponsorName={sponsorName}
                ctaHref={ctaHref}
                handleImpression={handleImpression}
                handleCtaClick={handleCtaClick}
            />
        );
    }

    return (
        <DelayedImpressionObserver
            key={`sponsored-insurance-container-${vin}`}
            onChange={handleImpression}
            delay={0}
            id="sponsored-insurance-container"
        >
            <div
                data-cmp="sponsored-insurance-container"
            >
                <div>
                    <SubHeading size={300}>
                        Car Insurance Made Simple
                    </SubHeading>
                    Discover how easy it is to find the right auto insurance coverage. Get a quote for this vehicle in under 5 minutes.
                </div>

                <div className="padding-top-3">

                    <img
                        height="20px"
                        src={`https://www.autotrader.com/cm-api/content/static/img/icon/logos/${logoMap[sponsorName?.toLowerCase()]}`}
                        alt={`${sponsorName} Logo`}
                        className="margin-right-2 margin-right-sm-3"
                    />

                    <Button
                        bsStyle="default"
                        bsSize="small"
                        className=""
                        onClick={handleCtaClick}
                        href={ctaHref}
                        target="_blank"
                    >
                        Get a Quote
                        <Glyphicon
                            glyph="offsite"
                            className="margin-left-1"
                        />
                    </Button>
                </div>

            </div>
        </DelayedImpressionObserver>
    );
}, (prevProps, nextProps) => prevProps.vin === nextProps.vin && prevProps.zip === nextProps.zip);

export default SponsoredInsuranceContainer;
